import { useCallback, useState } from 'react';
import { message } from 'antd';
import { useTranslation } from 'react-i18next';

import type { TaskStore } from '../../stores/kanban/TaskStore';
import { isStatusNotEmptyError } from '../../stores/kanban/statusErrors';

export type MoveRequest = { fromStatusId: number; taskCount: number };

/**
 * Column delete with the non-empty guard: `requestDelete` deletes an empty column outright; for a
 * non-empty one the store throws `StatusNotEmptyError` and `moveRequest` becomes non-null so the
 * caller renders `MoveTasksModal`; `confirmMove` retries with the chosen target.
 */
export function useDeleteStatus(store: TaskStore) {
  const { t } = useTranslation();
  const [moveRequest, setMoveRequest] = useState<MoveRequest | null>(null);

  const run = useCallback(
    async (statusId: number, moveTasksTo?: number): Promise<boolean> => {
      try {
        const ok = await store.deleteStatus(statusId, moveTasksTo);
        if (ok) {
          message.success(t('ColumnDeleted'));
        } else {
          message.error(t('FailedToDeleteColumn'));
        }
        return ok;
      } catch (error) {
        if (isStatusNotEmptyError(error)) {
          setMoveRequest({ fromStatusId: statusId, taskCount: error.taskCount });
          return false;
        }
        console.error('Failed to delete status:', error);
        message.error(t('FailedToDeleteColumn'));
        return false;
      }
    },
    [store, t],
  );

  const requestDelete = useCallback((statusId: number) => run(statusId), [run]);

  const confirmMove = useCallback(
    async (targetStatusId: number): Promise<boolean> => {
      if (!moveRequest) return false;
      const ok = await run(moveRequest.fromStatusId, targetStatusId);
      if (ok) setMoveRequest(null);
      return ok;
    },
    [moveRequest, run],
  );

  const cancelMove = useCallback(() => setMoveRequest(null), []);

  return { requestDelete, moveRequest, confirmMove, cancelMove };
}
